import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    CheckCircleOutline,
    MoreVert,
    PendingActionsOutlined
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Tab,
    Tabs
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePagination from '../../../../hooks/usePagination';
import { ackAlert, getAlerts } from '../../../../services/alerts';
import { fetchRecord } from '../../../../services/records';

const priorityByType = {
    FALL: { color: '#D32F2F', background: '#FDECEC', level: 'high' },
    HR_OVER_LIMIT: { color: '#D32F2F', background: '#FDECEC', level: 'high' },
    HR_LOW_EFFORT: { color: '#A86200', background: '#FFF4DE', level: 'moderate' }
};

const AlertActions = ({ alert, isPendingAlert, patientUuid, onActionError }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [openingSession, setOpeningSession] = useState(false);

    const acknowledgeMutation = useMutation({
        mutationFn: ackAlert,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['patient-alerts-history', patientUuid]
            });
            queryClient.invalidateQueries({
                queryKey: ['patient-alerts-summary', patientUuid]
            });
            queryClient.invalidateQueries({ queryKey: ['alerts'] });
        },
        onError: onActionError
    });

    const closeMenu = () => setAnchorEl(null);

    const viewSession = async () => {
        if (!alert.session) return;

        closeMenu();
        setOpeningSession(true);

        try {
            const { data: record } = await fetchRecord(alert.session);
            navigate('/users/user/record', {
                state: {
                    record,
                    from: location,
                    seeUserState: { selected: 7, expanded: false }
                }
            });
        } catch {
            onActionError();
        } finally {
            setOpeningSession(false);
        }
    };

    const acknowledge = async () => {
        setConfirmOpen(false);
        await acknowledgeMutation.mutateAsync(alert.uuid).catch(() => undefined);
    };

    if (openingSession) return <CircularProgress size={20} />;

    return (
        <>
            <IconButton
                size="small"
                aria-label={t('userPage.overview.alertList.actions')}
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                <MoreVert fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                <MenuItem onClick={viewSession} disabled={!alert.session}>
                    {t('alerts.viewSession')}
                </MenuItem>
                {isPendingAlert && (
                    <MenuItem
                        onClick={() => {
                            closeMenu();
                            setConfirmOpen(true);
                        }}
                    >
                        {t('alerts.markAsAnalyzed')}
                    </MenuItem>
                )}
            </Menu>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>{t('alerts.confirmAnalyzeDialog')}</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        onClick={acknowledge}
                        disabled={acknowledgeMutation.isPending}
                        autoFocus
                    >
                        {t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

AlertActions.propTypes = {
    alert: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        session: PropTypes.string
    }).isRequired,
    isPendingAlert: PropTypes.bool.isRequired,
    patientUuid: PropTypes.string.isRequired,
    onActionError: PropTypes.func.isRequired
};

const PatientAlertsTable = ({ patientUuid, status }) => {
    const { t } = useTranslation();
    const [actionError, setActionError] = useState(false);
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination(0, 10);
    const ackPending = status === 'pending';

    const { data, isPending, isError, refetch } = useQuery({
        queryKey: [
            'patient-alerts-history',
            patientUuid,
            ackPending,
            { paginationModel }
        ],
        queryFn: () =>
            getAlerts({
                patient: patientUuid,
                ack_pending: ackPending,
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page),
                qi: null,
                df: null
            }),
        placeholderData: keepPreviousData
    });

    useEffect(() => {
        if (data) resolveHeader(data.headers, paginationModel.page);
    }, [data, paginationModel.page, resolveHeader]);

    const rows = Array.isArray(data?.data)
        ? [...data.data].sort((firstAlert, secondAlert) => {
              const firstTimestamp = dayjs(firstAlert.ts);
              const secondTimestamp = dayjs(secondAlert.ts);

              if (!firstTimestamp.isValid()) return 1;
              if (!secondTimestamp.isValid()) return -1;

              return secondTimestamp.valueOf() - firstTimestamp.valueOf();
          })
        : [];
    const columns = [
        {
            field: 'priority',
            headerName: t('userPage.alertsPage.columns.priority'),
            minWidth: 130,
            flex: 0.7,
            sortable: false,
            renderCell: ({ row }) => {
                const priority = priorityByType[row.type] || {
                    color: '#A86200',
                    background: '#FFF4DE',
                    level: 'moderate'
                };

                return (
                    <Chip
                        size="small"
                        label={t(`userPage.alertsPage.priority.${priority.level}`)}
                        sx={{
                            minWidth: 86,
                            color: priority.color,
                            bgcolor: priority.background,
                            fontWeight: 700
                        }}
                    />
                );
            }
        },
        {
            field: 'ts',
            headerName: t('userPage.alertsPage.columns.date'),
            minWidth: 190,
            flex: 1,
            valueFormatter: (value) =>
                value && dayjs(value).isValid()
                    ? dayjs(value).format('DD/MM/YYYY, HH:mm')
                    : '—'
        },
        {
            field: 'type',
            headerName: t('userPage.alertsPage.columns.reason'),
            minWidth: 260,
            flex: 1.5,
            valueFormatter: (value) =>
                t(`alerts.types.${value}`, { defaultValue: value || '—' })
        },
        {
            field: 'status',
            headerName: t('userPage.alertsPage.columns.status'),
            minWidth: 140,
            flex: 0.8,
            sortable: false,
            renderCell: () => (
                <Chip
                    size="small"
                    variant="outlined"
                    color={ackPending ? 'warning' : 'success'}
                    label={t(
                        ackPending
                            ? 'userPage.alertsPage.pending'
                            : 'userPage.alertsPage.analyzed'
                    )}
                />
            )
        },
        {
            field: 'ack_role',
            headerName: t('userPage.alertsPage.columns.analyzedBy'),
            minWidth: 190,
            flex: 1,
            sortable: false,
            valueGetter: (value) => value?.name || '—'
        },
        {
            field: 'actions',
            headerName: t('userPage.alertsPage.columns.actions'),
            width: 90,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <AlertActions
                    alert={row}
                    isPendingAlert={ackPending}
                    patientUuid={patientUuid}
                    onActionError={() => setActionError(true)}
                />
            )
        }
    ];

    if (isError) {
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => refetch()}>
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                }
            >
                {t('userPage.alertsPage.loadError')}
            </Alert>
        );
    }

    return (
        <>
            {actionError && (
                <Alert
                    severity="error"
                    onClose={() => setActionError(false)}
                    sx={{ mb: 1.5 }}
                >
                    {t('userPage.overview.alertList.actionError')}
                </Alert>
            )}
            <Box sx={{ height: 570, width: '100%' }}>
                <DataGrid
                    rows={rows}
                    getRowId={(row) => row.uuid}
                    columns={columns}
                    loading={isPending}
                    paginationMode="server"
                    rowCount={count}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={rowsPerPageOptions}
                    disableRowSelectionOnClick
                    localeText={{ noRowsLabel: t('userPage.alertsPage.noRows') }}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeader': { color: '#FFFFFF' },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#FFFFFF',
                            fontWeight: 700
                        },
                        '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton': {
                            color: '#FFFFFF'
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: '#FAFBFC' },
                        '& .MuiDataGrid-cell': { borderColor: '#EEF1F3' }
                    }}
                />
            </Box>
        </>
    );
};

PatientAlertsTable.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['pending', 'analyzed']).isRequired
};

const PatientAlerts = ({ patientUuid }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('pending');

    return (
        <Card
            variant="outlined"
            sx={{
                borderColor: '#E0E5E9',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Tabs
                    value={status}
                    onChange={(_, value) => setStatus(value)}
                    aria-label={t('userPage.alertsPage.statusSelector')}
                    sx={{ mb: 2, minHeight: 42, '& .MuiTab-root': { minHeight: 42 } }}
                >
                    <Tab
                        value="pending"
                        icon={<PendingActionsOutlined fontSize="small" />}
                        iconPosition="start"
                        label={t('userPage.alertsPage.pending')}
                        sx={{ fontWeight: 700, textTransform: 'none' }}
                    />
                    <Tab
                        value="analyzed"
                        icon={<CheckCircleOutline fontSize="small" />}
                        iconPosition="start"
                        label={t('userPage.alertsPage.analyzed')}
                        sx={{ fontWeight: 700, textTransform: 'none' }}
                    />
                </Tabs>

                <PatientAlertsTable
                    key={status}
                    patientUuid={patientUuid}
                    status={status}
                />
            </CardContent>
        </Card>
    );
};

PatientAlerts.propTypes = {
    patientUuid: PropTypes.string.isRequired
};

export default PatientAlerts;
